import { Component, ContentChild, ContentChildren, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectAllToggleComponent } from './select-all-toggle/select-all-toggle.component';
import { FiltersComponent } from '../panel-hub/filters/filters.component';
import { SortMenuComponent } from '../panel-hub/sort-menu/sort-menu.component';
import { AppliedFilters } from './models/applied-filters';
import { SortCriteria } from './models/sort-criteria';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { EmptyResultsComponent } from '../empty-results/empty-results.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { PanelCardComponent } from './panel-card/panel-card.component';
import { PanelHubConfiguration } from './models/panel-hub-configuration';
import { PaginationState } from './models/pagination-state';
import { MessageConstants } from '../../shared/constants/message-constants';
import { TooltipModule } from 'primeng/tooltip';
import { LabelComponent } from '../label/label.component';
import { PaginatorComponent } from '../paginator/paginator.component';
import { PanelView, PanelViewLabelMap } from './enums/panel-view.enum';
import { BptMenuButtonModule } from 'bpt-ui-library/bpt-menu';
import { MenuItem } from 'primeng/api';
import { PanelHubConstants } from './constants/panel-hub-constants';
import { DefaultPaginationConfiguration, PaginationConfig } from './models/pagination-config';
import { QueryResult } from './models/query-result';
import { PanelHubEventType } from './enums/panel-hub-eventtype';
import { PanelModule } from "primeng/panel";
import { PanelHubStateChangedEvent } from './models/panel-hub-state-changed-event';
import { FilterEventHub } from './models/filter-event-hub';
import { PanelEventHub } from './models/panel-event-hub';
 
 
@Component({
  selector: 'app-panel-hub',
  standalone: true,
  imports: [
    CommonModule,
    SelectAllToggleComponent,
    FiltersComponent,
    SortMenuComponent,
    CheckboxModule,
    FormsModule,
    BptMenuButtonModule,
    EmptyResultsComponent,
    SpinnerComponent,
    PanelCardComponent,
    TooltipModule,
    LabelComponent,
    PaginatorComponent,
    PanelModule
  ],
  templateUrl: './panel-hub.component.html',
  styleUrls: ['./panel-hub.component.scss']
})
export class PanelHubComponent implements OnInit, OnChanges {
  @Input() config: PanelHubConfiguration = {
    title: '',
    standardFilterCount: PanelHubConstants.StandardFilterLength,
    enableToggling: false,
    enableShowAllToggle: false,
    enableSelectAll: false,
    enableSelection: false,
    showSelectAll: false,
    enablePanelCardClick: false,
    enablePanelHighLight: false,
    enableExport: false,
    enableExpandCollapse: false,
    enableViewModes: false,
    enableAssignmentView: false,
    enableWhereAmIUsedView: false,
    paginationConfig: DefaultPaginationConfiguration
  };
  @Input() pagedData: QueryResult = { totalRecords: 0, data: [], pageNumber: 1, pageSize: 25 };
  @Input() activeView: PanelView = PanelView.Default;
  @Output() panelHubStateChanged = new EventEmitter<PanelHubStateChangedEvent>();
  @Output() exportClicked = new EventEmitter<void>();
  @Output() viewChanged = new EventEmitter<PanelView>();
  @Output() panelCardClickEvent = new EventEmitter<any>();
  @Output() panelEventHub = new EventEmitter<PanelEventHub>();
  // New: Named template references for reliable header/content separation
  @ContentChild('headerTemplate') headerTemplate!: TemplateRef<any>;
  @ContentChild('contentTemplate') contentTemplate!: TemplateRef<any>;
 
  showLoader: boolean = false;
  currentPage = 0;
  resetFilters: boolean = false;
  isShowAllSelected: boolean = false;
  activePanelCard: number = -1;
  menuItems: MenuItem[] = [];
  currentView: PanelView = PanelView.Default;
 
  parentSelection: boolean = false;
  isSelectAllAcrossPages: boolean = false;
  // Track selection by ID for persistence across pages
  selectedCardIds: Set<any> = new Set();
  deselectedCardIds: Set<any> = new Set();
  filterEventHub!: FilterEventHub;
 
  private currentFilters: AppliedFilters | null = null;
  private currentSort: SortCriteria | null = null;
  private currentPagination: PaginationState | null = null;
 
  constructor() { }
 
  ngOnInit() {
    this.setMenuItems();
    this.currentView = this.activeView;
    if (this.config.enableViewModes) {
      this.updateViewIcons(this.currentView);
    }
    this.panelEventHub.emit({
      startLoader: () => this.requestStartLoader(),
      stopLoader: () => this.requestStopLoader(),
      resetConfigurations : () => this.resetPanelHubConfigurations(),
      resetShowAll : () => this.isShowAllSelected = false
 
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // When pagedData changes, set all items to collapsed by default if toggling is enabled
    if (changes['pagedData'] && this.config.enableToggling) {
      this.initializeCollapsedState();
    }
      if (changes['activeView'] && changes['activeView'].currentValue !== undefined) {
      this.currentView = changes['activeView'].currentValue;
  }
  }
 
  private initializeCollapsedState() {
    if (this.pagedData?.data) {
      this.pagedData.data.forEach((item: any) => item.collapsed = true);
    }
  }
  private getItemId(item: any): any {
    const idField = this.config.selectionIdField || 'id';
    return item[idField];
  }
  selectAllToggle(originEvent: any) {
    const checked = originEvent.checked;
    this.parentSelection = checked;
    this.selectedCardIds.clear();
    this.deselectedCardIds.clear();
    this.isSelectAllAcrossPages = checked;
    this.emitHubEvent(PanelHubEventType.ItemSelectionChanged);
  }
  panelCardItemSelectionChange(item: any, event: any, index: number) {
    const isChecked = event.checked;
    const itemId = this.getItemId(item);
    
    if (this.isSelectAllAcrossPages) {
      isChecked ? this.deselectedCardIds.delete(itemId) : this.deselectedCardIds.add(itemId);
      this.parentSelection = this.deselectedCardIds.size === 0;
    } else {
      isChecked ? this.selectedCardIds.add(itemId) : this.selectedCardIds.delete(itemId);
      this.parentSelection = this.selectedCardIds.size === this.pagedData.totalRecords;
    }
    this.emitHubEvent(PanelHubEventType.ItemSelectionChanged);
  }
 
  checkForDisable(item: any): boolean {
    return this.isItemDisabled(item);
  }
  isItemDisabled(item: any) {
    return false;
  }
 
  onPanelCardClick = (item: any, index: number) => {
    if (this.config.enablePanelCardClick) {
      if (this.config.enablePanelHighLight) {
        this.activePanelCard = index
      };
      this.panelCardClickEvent.emit(item);
    }
  }
 
  onFiltersChange(event: AppliedFilters) {
    this.currentFilters = event;
    this.currentPage = 1;
    if (this.currentPagination) {
      this.currentPagination.pageNumber = 1;
    }
    this.clearSelection();
    this.emitHubEvent(PanelHubEventType.FilterChanged);
  }
 
  onFilterEventHubInitialized(event: FilterEventHub) {
    this.filterEventHub = event;
  }
  onClearAllFilters() {
    this.clearSelection();
  }

  public clearSelection() {
    this.selectedCardIds.clear();
    this.deselectedCardIds.clear();
    this.parentSelection = false;
    this.isSelectAllAcrossPages = false;
    this.emitHubEvent(PanelHubEventType.ItemSelectionChanged);
  }

  onSortChange(event: SortCriteria) {
    this.currentSort = event;
    this.emitHubEvent(PanelHubEventType.SortChanged);
  }
 
  onPageChange(event: PaginationState) {
    this.currentPagination = event;
    this.currentPage = event.pageNumber;
    this.emitHubEvent(PanelHubEventType.PaginationChanged);
  }
 
  onShowAllChange() {
    this.resetPanelConfigurations();
    this.emitHubEvent(PanelHubEventType.ShowAllChanged);
  }
 
  expandAll() {
    this.pagedData.data.forEach((item: any) => item.collapsed = false);
  }
  collapseAll() {
    this.pagedData.data.forEach((item: any) => item.collapsed = true);
  }
  private emitHubEvent(eventType: PanelHubEventType) {
    const event: PanelHubStateChangedEvent = {
      type: eventType,
      state: {
        filters: this.currentFilters,
        sort: this.currentSort,
        pagination: this.currentPagination,
        selectedItems: Array.from(this.selectedCardIds),
        deselectedItems: Array.from(this.deselectedCardIds),
        isSelectAllAcrossPages: this.isSelectAllAcrossPages,
        isShowAllChanged: this.isShowAllSelected
      }
    };
    this.panelHubStateChanged.emit(event);
  }

  getSelecteditems(item: any, i: number): boolean {
    const itemId = this.getItemId(item);
    if (this.isSelectAllAcrossPages) {
      // If select all is active, item is selected unless it's in the deselected set
      return !this.deselectedCardIds.has(itemId);
    } else {
      // Otherwise, item is selected only if it's in the selected set
      return this.selectedCardIds.has(itemId);
    }
  }
  getToolTipForDisabledCheckBox(item: any): string {
    // TO DO: Need to be configured
    // and updated based on feature, Message should come from feature component
    // No Logic here.
    return MessageConstants.BlankToolTipConstant;
  }
 
  private setMenuItems() {
    this.menuItems = [];
    if (this.config.enableExport) {
      this.menuItems.push({
        label: 'Export',
        icon: 'fa fa-file-excel',
        command: () => this.exportClicked.emit()
      });
    }
 
    if (this.config.enableExpandCollapse) {
      this.menuItems.push(
        {
          label: 'Expand All',
          icon: 'fa fa-plus',
          command: () => this.expandAll()
        },
        {
          label: 'Collapse All',
          icon: 'fa fa-minus',
          command: () => this.collapseAll()
        }
      );
    }
 
    if (this.config.enableViewModes) {
      this.menuItems.push({ separator: true });
      this.menuItems.push({
        label: PanelViewLabelMap[PanelView.Default],
        icon: 'fa fa-table',
        command: () => this.changeView(PanelView.Default)
      });

      if (this.config.enableAssignmentView) {
        this.menuItems.push({
          label: PanelViewLabelMap[PanelView.Assignment],
          icon: 'fa fa-tasks',
          command: () => this.changeView(PanelView.Assignment)
        });
      }

      if (this.config.enableWhereAmIUsedView) {
        this.menuItems.push({
          label: PanelViewLabelMap[PanelView.WhereAmIUsed],
          icon: 'fa fa-sitemap',
          command: () => this.changeView(PanelView.WhereAmIUsed)
        });
      }
    }
  }
 
  private changeView(view: PanelView) {
    this.currentView = view;
    this.updateViewIcons(view);
    this.resetPanelConfigurations();
    this.viewChanged.emit(view);
  }
 
  private updateViewIcons(activeView: PanelView): void {
    (Object.values(PanelView) as PanelView[]).forEach(view => {
      const label = PanelViewLabelMap[view];
      const item = this.menuItems.find((menuItem: MenuItem) => menuItem.label === label);
      if (!item) return;
      if (activeView === view) {
        item.styleClass = `${PanelHubConstants.MenuItemBaseClass} ${PanelHubConstants.SelectedMenuItemStyleClass}`;
        item.icon = PanelHubConstants.SelectedMenuItemIcon;
      } else {
        item.styleClass = PanelHubConstants.MenuItemBaseClass;
        item.icon = PanelHubConstants.EmptyString;
      }
    });
  }
 
  resetPanelConfigurations() {
    this.resetFilters = true;
    this.currentFilters = null;
    this.currentSort = null;
    this.currentPagination = null;
    this.selectedCardIds.clear();
    this.deselectedCardIds.clear();
    this.parentSelection = false;
    this.isSelectAllAcrossPages = false;
    this.currentPage = 0;
    this.filterEventHub.resetFilters();
    if (this.config.enableViewModes) {
      this.updateViewIcons(this.currentView);
    }
  }
  private requestStartLoader() { this.showLoader = true; }
  private requestStopLoader() { this.showLoader = false; }
  private resetPanelHubConfigurations() {this.resetPanelConfigurations();}
 
}
